import axios from "axios";
import {
  CLEAR_CART_ERROR,
  ADD_TO_CART_FAIL,
  ADD_TO_CART_REQUEST,
  ADD_TO_CART_SUCCESS,
  ALL_CART_ITEM_FAIL,
  ALL_CART_ITEM_REQUEST,
  ALL_CART_ITEM_SUCCESS,
  REMOVE_FROM_CART_FAIL,
  REMOVE_FROM_CART_REQUEST,
  REMOVE_FROM_CART_SUCCESS,
  SAVE_SHIPPING_INFO,
  SAVE_BUY_ITEM_INFO,
} from "../constants/cartConstants";

export const GUEST_CART_STORAGE_KEY = "im_guest_cart";

export const getGuestCartFromStorage = () => {
  try {
    const raw = localStorage.getItem(GUEST_CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const saveGuestCartToStorage = (items) => {
  try {
    localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.error("Failed to save guest cart to localStorage", e);
  }
};

// Add to cart action (supports both guest and authenticated users)
export const cartAction =
  (id, quantity, size, colour, selectBox, productShippingCharges) =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: ADD_TO_CART_REQUEST,
      });

      const { data } = await axios.get(`/api/add-to-cart/product/${id}`);
      const prod = data.isDataGet;

      if (!prod) {
        throw new Error("Product details could not be retrieved.");
      }

      const effectivePrice =
        prod.discountActive === true
          ? Math.max((prod.price || 0) - (prod.discountPrice || 0), 0)
          : prod.price;

      const primaryImage =
        (prod.mainImage && prod.mainImage[0] && prod.mainImage[0].imageurl) ||
        (prod.image && prod.image[0] && prod.image[0].imageurl) ||
        "";

      const cartItemData = {
        product: prod._id,
        name: prod.name,
        price: effectivePrice,
        image: primaryImage,
        productStock: prod.productStock,
        size: Number(size) || 0,
        colour: colour || "Default",
        quantity: Number(quantity) || 1,
        selectBox: String(selectBox || "0"),
        productShippingCharges: productShippingCharges || 0,
      };

      const token = localStorage.getItem("token");
      const { isAuthenticated } = getState().user || {};

      if (!isAuthenticated && !token) {
        // Guest mode: update local storage
        const guestItems = getGuestCartFromStorage();
        const existingIdx = guestItems.findIndex(
          (item) =>
            String(item.product) === String(prod._id) &&
            String(item.size) === String(cartItemData.size) &&
            String(item.colour) === String(cartItemData.colour)
        );

        if (existingIdx > -1) {
          guestItems[existingIdx].quantity = Math.min(
            guestItems[existingIdx].quantity + cartItemData.quantity,
            prod.productStock
          );
          guestItems[existingIdx].price = effectivePrice;
        } else {
          guestItems.push({
            _id: "guest_" + Date.now() + "_" + Math.random().toString(36).substr(2, 6),
            ...cartItemData,
          });
        }

        saveGuestCartToStorage(guestItems);

        dispatch({
          type: ADD_TO_CART_SUCCESS,
          payload: "Item added to bag!",
        });

        dispatch({
          type: ALL_CART_ITEM_SUCCESS,
          payload: guestItems,
        });
        return;
      }

      // Authenticated mode: send to backend
      const config = {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };

      const res = await axios.post(
        `/api/create/cart`,
        { cartData: cartItemData },
        config
      );

      dispatch({
        type: ADD_TO_CART_SUCCESS,
        payload: res.data.msg || "Item added in cart !!",
      });

      dispatch(allcartItemAction());
    } catch (error) {
      dispatch({
        type: ADD_TO_CART_FAIL,
        payload: error.response?.data?.msg || error.message || "Failed to add item to bag.",
      });
    }
  };

// Remove from cart action (supports both guest and authenticated users)
export const cartRemoveAction = (id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: REMOVE_FROM_CART_REQUEST,
    });

    const token = localStorage.getItem("token");
    const { isAuthenticated } = getState().user || {};

    if (!isAuthenticated && !token) {
      let guestItems = getGuestCartFromStorage();
      guestItems = guestItems.filter(
        (item) => String(item.product) !== String(id) && String(item._id) !== String(id)
      );
      saveGuestCartToStorage(guestItems);

      dispatch({
        type: REMOVE_FROM_CART_SUCCESS,
        payload: "Item removed from bag",
      });

      dispatch({
        type: ALL_CART_ITEM_SUCCESS,
        payload: guestItems,
      });
      return;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    const { data } = await axios.delete(`/api/remove/cart/${id}`, config);

    dispatch({
      type: REMOVE_FROM_CART_SUCCESS,
      payload: data.msg || "Item removed",
    });

    dispatch(allcartItemAction());
  } catch (error) {
    dispatch({
      type: REMOVE_FROM_CART_FAIL,
      payload: error.response?.data?.msg || error.message || "Failed to remove item",
    });
  }
};

// Fetch all cart items (supports both guest and authenticated users)
export const allcartItemAction = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: ALL_CART_ITEM_REQUEST,
    });

    const token = localStorage.getItem("token");
    const { isAuthenticated } = getState().user || {};

    if (!isAuthenticated && !token) {
      const guestItems = getGuestCartFromStorage();
      return dispatch({
        type: ALL_CART_ITEM_SUCCESS,
        payload: guestItems,
      });
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    const { data } = await axios.get(`/api/cart`, config);

    dispatch({
      type: ALL_CART_ITEM_SUCCESS,
      payload: data.msg || [],
    });
  } catch (error) {
    const guestItems = getGuestCartFromStorage();
    if (guestItems.length > 0) {
      dispatch({
        type: ALL_CART_ITEM_SUCCESS,
        payload: guestItems,
      });
    } else {
      dispatch({
        type: ALL_CART_ITEM_FAIL,
        payload: error.response?.data?.msg || error.message || "Failed to load cart items",
      });
    }
  }
};

// Merge guest cart into user's server cart after login
export const mergeGuestCartAction = () => async (dispatch) => {
  try {
    const guestItems = getGuestCartFromStorage();
    if (!guestItems || guestItems.length === 0) {
      dispatch(allcartItemAction());
      return;
    }

    const token = localStorage.getItem("token");
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    const { data } = await axios.post(
      `/api/cart/merge-guest`,
      { guestItems },
      config
    );

    localStorage.removeItem(GUEST_CART_STORAGE_KEY);

    if (data && data.cart) {
      dispatch({
        type: ALL_CART_ITEM_SUCCESS,
        payload: data.cart,
      });
    } else {
      dispatch(allcartItemAction());
    }
  } catch (err) {
    console.error("Failed to merge guest cart", err);
    dispatch(allcartItemAction());
  }
};

export const clearErrorAction = () => async (dispatch) => {
  dispatch({
    type: CLEAR_CART_ERROR,
  });
};

// Saving cart shipping info
export const savingCartInfoAction = (data) => async (dispatch) => {
  dispatch({
    type: SAVE_SHIPPING_INFO,
    payload: data,
  });
  localStorage.setItem("shippingInfo", JSON.stringify(data));
};

export const savingBuyItemInfoAction = (data) => (dispatch) => async (getState) => {
  dispatch({
    type: SAVE_BUY_ITEM_INFO,
    payload: data,
  });
  localStorage.setItem("cartItems", JSON.stringify(getState().cart?.cartItems || []));
};
